import React from "react";
import type { ThemeTokens, Tweaks, SectionCfg } from "../types";
import { useTemplateStyles } from "../useTemplateStyles";
import "../template.css";

type HeroStyle = "split" | "centered" | "left" | "magazine" | "overlay";
type ImgStyle = "wide" | "fullbleed" | "square" | "portrait";
type AuthorStyle = "inline" | "card" | "minimal" | "banner";

function SkelLine({
  className = "",
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <span
      className={`mte-tpl-skel-line ${className}`.trim()}
      style={style}
      aria-hidden
    />
  );
}

/** Minimal shell — avoids importing TemplateSections (circular with PostHero). */
function ShellLite({
  T,
  children,
  fullBleed,
  independentShellClassName,
  page = "journal-post",
  section = "hero",
}: {
  T: ThemeTokens;
  children: React.ReactNode;
  fullBleed?: boolean;
  independentShellClassName?: string;
  page?: string;
  section?: string;
}) {
  const shellPad = fullBleed ? "0" : `0 ${T.gutter} 0`;
  const shellStyle = React.useMemo(
    () =>
      ({
        ["--tpl-shell-bg" as string]: T.bg,
        ["--tpl-shell-fg" as string]: T.ink,
        ["--tpl-shell-muted" as string]: T.muted,
        ["--tpl-shell-pad" as string]: shellPad,
      }) as React.CSSProperties,
    [T.bg, T.ink, T.muted, shellPad],
  );
  const shellRef = useTemplateStyles<HTMLElement>(shellStyle);
  const base = independentShellClassName || "mte-tpl-shell";
  return (
    <section
      ref={shellRef as React.Ref<HTMLElement>}
      className={[
        independentShellClassName ? "mte-tpl-section-shell" : "mte-tpl-shell",
        base,
        `${base}--page-${page}`,
        `${base}--section-${section}`,
        `${base}--${page}-${section}`,
      ]
        .filter(Boolean)
        .join(" ")}
      data-template-shell="true"
      data-template-shell-tone="bg"
      {...(fullBleed ? { "data-template-shell-fullbleed": "true" } : {})}
    >
      {children}
    </section>
  );
}

function heroImgClass(imgStyle: ImgStyle): string {
  if (imgStyle === "fullbleed") return "mte-tpl-ph-img-fullbleed";
  if (imgStyle === "square") return "mte-tpl-ph-img-square";
  if (imgStyle === "portrait") return "mte-tpl-ph-img-portrait";
  return "mte-tpl-ph-img-wide";
}

/**
 * Layout-matched PostHero skeleton — mirrors live `style` / `imgStyle` from tweaks.
 */
export function JournalPostHeroSkeleton({
  T,
  cfg,
  style: styleProp,
}: {
  T: ThemeTokens;
  cfg?: SectionCfg;
  style?: string;
  /** @deprecated Image shape control removed; always wide. */
  imgStyle?: string;
}) {
  const style = ((styleProp || cfg?.style || "split") as HeroStyle) || "split";
  const imgStyle: ImgStyle = "wide";
  const mob = T.device === "mobile";
  const imgCls = heroImgClass(imgStyle);
  const flush = false;

  if (style === "split") {
    return (
      <ShellLite
        T={T}
        fullBleed
        independentShellClassName="mte-tpl-post-hero-shell"
        section="hero"
      >
        <div
          className={`mte-tpl-ph-split${mob ? " mte-tpl-ph-split--mob" : ""} mte-tpl-article-skel`}
          aria-busy="true"
        >
          <div className="mte-tpl-ph-split-copy">
            <SkelLine className="mte-tpl-skel-line--display mte-tpl-article-skel-title mte-tpl-article-skel-title--start" />
            <SkelLine className="mte-tpl-skel-line--lg mte-tpl-article-skel-excerpt mte-tpl-article-skel-excerpt--start" />
            <SkelLine className="mte-tpl-skel-line--sm mte-tpl-article-skel-meta mte-tpl-article-skel-meta--start" />
          </div>
          <div
            className="mte-tpl-ph-split-media mte-tpl-skel-fill"
            aria-hidden
          />
        </div>
      </ShellLite>
    );
  }

  if (style === "overlay") {
    return (
      <ShellLite
        T={T}
        fullBleed
        independentShellClassName="mte-tpl-post-hero-shell"
        section="hero"
      >
        <div className="mte-tpl-ph-ov-wrap mte-tpl-article-skel" aria-busy="true">
          <div className="mte-tpl-ph-ov-img mte-tpl-skel-fill" aria-hidden />
          <div className="mte-tpl-ph-ov-grad" />
          <div className="mte-tpl-ph-ov-inner">
            <SkelLine className="mte-tpl-skel-line--sm mte-tpl-article-skel-eyebrow" />
            <SkelLine className="mte-tpl-skel-line--display mte-tpl-article-skel-title mte-tpl-article-skel-title--ov" />
            <SkelLine className="mte-tpl-skel-line--lg mte-tpl-article-skel-excerpt" />
            <SkelLine className="mte-tpl-skel-line--sm mte-tpl-article-skel-meta" />
          </div>
        </div>
      </ShellLite>
    );
  }

  if (style === "magazine") {
    return (
      <ShellLite
        T={T}
        independentShellClassName="mte-tpl-post-hero-shell"
        section="hero"
      >
        <div className="mte-tpl-ph-mag-grid mte-tpl-article-skel" aria-busy="true">
          <div>
            <SkelLine className="mte-tpl-skel-line--sm mte-tpl-article-skel-eyebrow mte-tpl-article-skel-eyebrow--start" />
            <SkelLine className="mte-tpl-skel-line--display mte-tpl-article-skel-title mte-tpl-article-skel-title--start" />
            <SkelLine className="mte-tpl-skel-line--lg mte-tpl-article-skel-excerpt mte-tpl-article-skel-excerpt--start" />
            <SkelLine className="mte-tpl-skel-line--md mte-tpl-article-skel-excerpt mte-tpl-article-skel-excerpt--start" />
            <SkelLine className="mte-tpl-skel-line--sm mte-tpl-article-skel-meta mte-tpl-article-skel-meta--start" />
          </div>
          <div className="mte-tpl-ph-mag-aside">
            <div
              className="mte-tpl-ph-mag-aside-img mte-tpl-skel-fill"
              aria-hidden
            />
          </div>
          {mob ? (
            <div className="mte-tpl-ph-mob-wide mte-tpl-skel-fill" aria-hidden />
          ) : null}
        </div>
      </ShellLite>
    );
  }

  if (style === "left") {
    return (
      <ShellLite
        T={T}
        independentShellClassName="mte-tpl-post-hero-shell"
        section="hero"
      >
        <div className="mte-tpl-article-skel" aria-busy="true">
          <div className="mte-tpl-ph-left-pad">
            <SkelLine className="mte-tpl-skel-line--sm mte-tpl-article-skel-eyebrow mte-tpl-article-skel-eyebrow--start" />
            <SkelLine className="mte-tpl-skel-line--display mte-tpl-article-skel-title mte-tpl-article-skel-title--start" />
            <SkelLine className="mte-tpl-skel-line--lg mte-tpl-article-skel-excerpt mte-tpl-article-skel-excerpt--start" />
            <SkelLine className="mte-tpl-skel-line--sm mte-tpl-article-skel-meta mte-tpl-article-skel-meta--start" />
          </div>
          <div
            className={`mte-tpl-ph-img-slot${flush ? " mte-tpl-ph-img-slot--flush" : ""}`}
          >
            <div className={`${imgCls} mte-tpl-skel-fill`} aria-hidden />
          </div>
        </div>
      </ShellLite>
    );
  }

  return (
    <ShellLite
      T={T}
      independentShellClassName="mte-tpl-post-hero-shell"
      section="hero"
    >
      <div className="mte-tpl-article-skel" aria-busy="true">
        <div className="mte-tpl-ph-ctr-pad">
          <SkelLine className="mte-tpl-skel-line--sm mte-tpl-article-skel-eyebrow" />
          <SkelLine className="mte-tpl-skel-line--display mte-tpl-article-skel-title" />
          <SkelLine className="mte-tpl-skel-line--lg mte-tpl-article-skel-excerpt" />
          <SkelLine className="mte-tpl-skel-line--sm mte-tpl-article-skel-meta" />
        </div>
        <div
          className={`mte-tpl-ph-img-slot mte-tpl-ph-img-slot--lg${
            flush ? " mte-tpl-ph-img-slot--flush" : ""
          }`}
        >
          <div className={`${imgCls} mte-tpl-skel-fill`} aria-hidden />
        </div>
      </div>
    </ShellLite>
  );
}

function AuthorSkeleton({
  T,
  style,
}: {
  T: ThemeTokens;
  style: AuthorStyle;
}) {
  if (style === "card") {
    return (
      <ShellLite
        T={T}
        independentShellClassName="mte-tpl-post-author-shell"
        section="author"
      >
        <div className="mte-tpl-auth-card mte-tpl-article-skel" aria-busy="true">
          <div className="mte-tpl-auth-ava-lg mte-tpl-skel-fill" aria-hidden />
          <SkelLine
            className="mte-tpl-skel-line--sm"
            style={{ width: "5rem", marginInline: "auto" }}
          />
          <SkelLine
            className="mte-tpl-skel-line--lg"
            style={{ width: "9rem", marginInline: "auto", marginTop: 8 }}
          />
          <SkelLine
            className="mte-tpl-skel-line--md"
            style={{ width: "70%", marginInline: "auto", marginTop: 8 }}
          />
        </div>
      </ShellLite>
    );
  }
  if (style === "banner") {
    return (
      <ShellLite
        T={T}
        independentShellClassName="mte-tpl-post-author-shell"
        section="author"
      >
        <div className="mte-tpl-auth-ban-grid mte-tpl-article-skel" aria-busy="true">
          <div className="mte-tpl-auth-ban-ava mte-tpl-skel-fill" aria-hidden />
          <div>
            <SkelLine className="mte-tpl-skel-line--sm" style={{ width: "5rem" }} />
            <SkelLine
              className="mte-tpl-skel-line--lg"
              style={{ width: "40%", marginTop: 8 }}
            />
            <SkelLine
              className="mte-tpl-skel-line--md"
              style={{ width: "70%", marginTop: 8 }}
            />
          </div>
        </div>
      </ShellLite>
    );
  }
  if (style === "minimal") {
    return (
      <ShellLite
        T={T}
        independentShellClassName="mte-tpl-post-author-shell"
        section="author"
      >
        <div className="mte-tpl-auth-min-row mte-tpl-article-skel" aria-busy="true">
          <div className="mte-tpl-auth-ava-sm mte-tpl-skel-fill" aria-hidden />
          <div className="mte-tpl-auth-min-grow">
            <SkelLine className="mte-tpl-skel-line--sm" style={{ width: "5rem" }} />
            <SkelLine
              className="mte-tpl-skel-line--md"
              style={{ width: "8rem", marginTop: 6 }}
            />
          </div>
        </div>
      </ShellLite>
    );
  }
  return (
    <ShellLite
      T={T}
      independentShellClassName="mte-tpl-post-author-shell"
      section="author"
    >
      <div className="mte-tpl-auth-inline-grid mte-tpl-article-skel" aria-busy="true">
        <div className="mte-tpl-auth-inline-ava mte-tpl-skel-fill" aria-hidden />
        <div>
          <SkelLine className="mte-tpl-skel-line--sm" style={{ width: "5rem" }} />
          <SkelLine
            className="mte-tpl-skel-line--md"
            style={{ width: "35%", marginTop: 8 }}
          />
          <SkelLine
            className="mte-tpl-skel-line--sm"
            style={{ width: "55%", marginTop: 8 }}
          />
        </div>
      </div>
    </ShellLite>
  );
}

function BodySkeleton({ T }: { T: ThemeTokens }) {
  return (
    <ShellLite
      T={T}
      independentShellClassName="mte-tpl-post-body-shell"
      section="body"
    >
      <div className="mte-tpl-article-skel mte-tpl-article-skel-body" aria-busy="true">
        <SkelLine className="mte-tpl-skel-line--lg" style={{ width: "100%" }} />
        <SkelLine className="mte-tpl-skel-line--lg" style={{ width: "96%", marginTop: 12 }} />
        <SkelLine className="mte-tpl-skel-line--lg" style={{ width: "92%", marginTop: 12 }} />
        <SkelLine className="mte-tpl-skel-line--lg" style={{ width: "88%", marginTop: 12 }} />
        <SkelLine className="mte-tpl-skel-line--md" style={{ width: "64%", marginTop: 12 }} />
        <SkelLine className="mte-tpl-skel-line--lg" style={{ width: "100%", marginTop: 28 }} />
        <SkelLine className="mte-tpl-skel-line--lg" style={{ width: "94%", marginTop: 12 }} />
        <SkelLine className="mte-tpl-skel-line--lg" style={{ width: "90%", marginTop: 12 }} />
        <SkelLine className="mte-tpl-skel-line--md" style={{ width: "50%", marginTop: 12 }} />
      </div>
    </ShellLite>
  );
}

/**
 * Full blog-article page loader. Hero / author layouts follow
 * `tweaks.pages.journalPost.cfg` (same keys as the Tweaks panel).
 */
export function JournalPostArticleLoader({
  T,
  tweaks,
}: {
  T: ThemeTokens;
  tweaks?: Tweaks;
}) {
  const pageCfg = tweaks?.pages?.journalPost?.cfg || {};
  const heroCfg = (pageCfg.hero || {}) as SectionCfg;
  const authorCfg = (pageCfg.author || {}) as SectionCfg;
  const heroStyle = String(heroCfg.style || "split");
  const imgStyle = "wide";
  const authorStyle = (String(authorCfg.style || "inline") as AuthorStyle) || "inline";

  return (
    <div
      className="mte-tpl-sections-stack mte-tpl-article-skel-page"
      aria-busy="true"
      aria-label="Loading article"
    >
      <div
        className="mte-tpl-section mte-tpl-section--page-journal-post mte-tpl-section--id-hero mte-tpl-section--type-posthero mte-tpl-section--entered"
        data-template-page="journalPost"
        data-template-section-id="hero"
        data-template-section-type="PostHero"
      >
        <JournalPostHeroSkeleton
          T={T}
          cfg={heroCfg}
          style={heroStyle}
          imgStyle={imgStyle}
        />
      </div>
      <div
        className="mte-tpl-section mte-tpl-section--page-journal-post mte-tpl-section--id-author mte-tpl-section--type-postauthor mte-tpl-section--entered"
        data-template-page="journalPost"
        data-template-section-id="author"
        data-template-section-type="PostAuthor"
      >
        <AuthorSkeleton T={T} style={authorStyle} />
      </div>
      <div
        className="mte-tpl-section mte-tpl-section--page-journal-post mte-tpl-section--id-body mte-tpl-section--type-postbody mte-tpl-section--entered"
        data-template-page="journalPost"
        data-template-section-id="body"
        data-template-section-type="PostBody"
      >
        <BodySkeleton T={T} />
      </div>
    </div>
  );
}
